import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  Menu,
  MenuItem,
  Radio,
  Typography,
  useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import { selectDefaultDateFormat } from "redux/preferencesSlice";
import {
  updateFieldInSignRequest,
  updateFieldOfSelectedFile,
} from "redux/appSlice";
import { FileField, RequestParticipant } from "models/interfaces";
import strings from "global/constants/StringConstants";
import { capitalizeText } from "helpers/methods";
import {
  Fields,
  fieldColor,
  getFieldSizeMobile,
  getNextRadioGroupId,
} from "../../SignRequestHelper";
import { pressableProps } from "../mobileA11y";
import MobileSignatureIcon from "../../../../assets/icons/MobileFieldIcons/MobileSignature.svg";
import MobileInitialsIcon from "../../../../assets/icons/MobileFieldIcons/MobileInitials.svg";
import MobileTextIcon from "../../../../assets/icons/MobileFieldIcons/MobileText.svg";
import MobileCheckboxIcon from "../../../../assets/icons/MobileFieldIcons/MobileCheckbox.svg";

const MOBILE_FIELD_ICONS: Record<string, string> = {
  [strings.signature]: MobileSignatureIcon,
  [strings.initials]: MobileInitialsIcon,
  [strings.text]: MobileTextIcon,
  [strings.checkbox]: MobileCheckboxIcon,
};

const getInitials = (name?: string) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

/**
 * "Type" properties sheet (Figma node 1345-6963). Opens from the "Type" chip of
 * the field options bar. Lets the user re-assign the selected field to a signer
 * and change its field type, then Update to apply.
 */
const MobileFieldTypeSheet = ({
  open,
  onClose,
  field,
}: {
  open: boolean;
  onClose: () => void;
  field: FileField | null;
}) => {
  const theme = useTheme();
  const m = theme.palette.mobile;
  const dispatch = useAppDispatch();
  const { signRequest, selectedFile } = useAppSelector(
    (s: RootState) => s.app,
  );
  const defaultDateFormat = useAppSelector(selectDefaultDateFormat);

  const participants: RequestParticipant[] = (signRequest.signers || []).filter(
    (s: RequestParticipant) => !s.cc,
  );

  const [pendingType, setPendingType] = useState<string>("");
  const [pendingSignerId, setPendingSignerId] = useState<number | undefined>(
    undefined,
  );
  const [assignAnchor, setAssignAnchor] = useState<null | HTMLElement>(null);

  // Re-seed the pending selections from the field each time the sheet opens.
  useEffect(() => {
    if (open && field) {
      setPendingType(field.type);
      setPendingSignerId(field.signerIndex);
    }
  }, [open, field?.id]);

  const fieldTypes = Object.values(Fields(theme)).flat() as {
    type: string;
    icon: string;
  }[];

  const assignedParticipant = participants.find(
    (p) => p.id === pendingSignerId,
  );

  const handleClear = () => {
    // Revert the pending selections back to the field's current values.
    if (field) {
      setPendingType(field.type);
      setPendingSignerId(field.signerIndex);
    }
  };

  const handleUpdate = () => {
    if (!field) return;
    const patch: any = { id: field.id };

    const signerChanged =
      pendingSignerId != null && pendingSignerId !== field.signerIndex;
    if (signerChanged) {
      patch.signerIndex = pendingSignerId;
      patch.color = fieldColor[pendingSignerId as number];
    }

    const typeChanged = pendingType && pendingType !== field.type;
    if (typeChanged) {
      patch.type = pendingType;
      patch.existingField = false;

      // Recompute the field's size for the new type from the page's natural
      // dimensions (percent sizes are independent of the rendered page size).
      const natural = selectedFile?.dimensions?.[field.page];
      if (natural?.x && natural?.y) {
        const [baseW, baseH] = getFieldSizeMobile(pendingType);
        const fieldPageWidth = (baseW / natural.x) * 100;
        const fieldPageHeight = (baseH / natural.y) * 100;
        patch.fieldPageWidth = fieldPageWidth;
        patch.fieldPageHeight = fieldPageHeight;
        patch.w = fieldPageWidth;
        patch.h = fieldPageHeight;
      }

      if (
        pendingType === strings.radioButton &&
        field.type !== strings.radioButton
      ) {
        patch.groupId = getNextRadioGroupId(selectedFile);
        patch.value = "false";
        patch.required = true;
      }
      if (
        (pendingType === strings.date || pendingType === strings.dateSigned) &&
        !field.dateFormat
      ) {
        patch.dateFormat = defaultDateFormat;
      }
      if (pendingType === strings.checkbox) {
        patch.required = false;
      }
    }

    if (patch.type !== undefined || patch.signerIndex !== undefined) {
      dispatch(updateFieldOfSelectedFile(patch));
      dispatch(updateFieldInSignRequest(patch));
    }
    onClose();
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { px: 2, pb: 3, borderTopLeftRadius: 0, borderTopRightRadius: 0 },
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": "field-type-sheet-title",
      }}
    >
      {/* Drag handle */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 1.5 }}>
        <Box
          aria-hidden="true"
          sx={{ width: 42, height: 4, borderRadius: 2, bgcolor: m.grey[50] }}
        />
      </Box>

      {/* Title + Clear */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography
          id="field-type-sheet-title"
          component="h2"
          sx={{ fontSize: "1.125rem", fontWeight: 700, color: m.base[900] }}
        >
          Type
        </Typography>
        <Typography
          {...pressableProps(handleClear, { label: "Clear changes" })}
          sx={{
            fontSize: "0.9375rem",
            fontWeight: 700,
            color: m.base[500],
            cursor: "pointer",
          }}
        >
          Clear
        </Typography>
      </Box>

      {/* ASSIGNED TO */}
      <Typography
        sx={{
          fontSize: "0.75rem",
          fontWeight: 500,
          color: m.grey[400],
          textTransform: "uppercase",
          mb: 0.5,
        }}
      >
        Assigned to
      </Typography>
      <Box
        {...(participants.length > 1
          ? pressableProps((e) => setAssignAnchor(e.currentTarget as HTMLElement), {
              label: `Assigned to ${assignedParticipant?.name || "Unassigned"}`,
              hasPopup: "menu",
              expanded: Boolean(assignAnchor),
            })
          : {})}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          border: `1px solid ${m.grey[50]}`,
          borderRadius: "8px",
          p: 2,
          cursor: participants.length > 1 ? "pointer" : "default",
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
          {assignedParticipant && (
            <Box
              aria-hidden="true"
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                bgcolor: assignedParticipant.color || m.base[100],
                color: m.surface.white,
                fontSize: "0.6875rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {getInitials(assignedParticipant.name)}
            </Box>
          )}
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 500,
              color: m.base[500],
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {assignedParticipant?.name || "Unassigned"}
          </Typography>
        </Box>
        <KeyboardArrowDownIcon sx={{ fontSize: 20, color: m.base[500] }} />
      </Box>

      <Menu
        anchorEl={assignAnchor}
        open={Boolean(assignAnchor)}
        onClose={() => setAssignAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { minWidth: 220, borderRadius: 2 } }}
        MenuListProps={{ "aria-label": "Assign field to signer" }}
      >
        {participants.map((p) => (
          <MenuItem
            key={p.id}
            selected={p.id === pendingSignerId}
            onClick={() => {
              setPendingSignerId(p.id);
              setAssignAnchor(null);
            }}
          >
            <Box
              aria-hidden="true"
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                bgcolor: p.color || m.base[100],
                color: m.surface.white,
                fontSize: "0.6875rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 1.25,
                flexShrink: 0,
              }}
            >
              {getInitials(p.name)}
            </Box>
            <Typography sx={{ fontSize: "0.875rem", color: m.base[500] }}>
              {p.name || "Unnamed signer"}
            </Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* FIELD TYPE */}
      <Typography
        sx={{
          fontSize: "0.75rem",
          fontWeight: 500,
          color: m.grey[400],
          textTransform: "uppercase",
          mb: 1,
        }}
      >
        Field Type
      </Typography>
      <Box
        sx={{
          maxHeight: "45vh",
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {fieldTypes.map((ft) => {
          const isSelected = pendingType === ft.type;
          return (
            <Box
              key={ft.type}
              onClick={() => setPendingType(ft.type)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                border: `1px solid ${m.grey[50]}`,
                borderRadius: "8px",
                p: 1.5,
                mb: 1.5,
                cursor: "pointer",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  bgcolor: m.ePurple[50],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <img
                  src={MOBILE_FIELD_ICONS[ft.type] || ft.icon}
                  alt={`${ft.type} icon`}
                  width={18}
                  height={18}
                  aria-hidden="true"
                />
              </Box>
              <Typography
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  fontSize: "0.9375rem",
                  fontWeight: 500,
                  color: m.base[900],
                }}
              >
                {capitalizeText(ft.type.replace(/_/g, " "))}
              </Typography>
              <Radio
                checked={isSelected}
                onChange={() => setPendingType(ft.type)}
                onClick={(e) => e.stopPropagation()}
                name="mobile-field-type"
                inputProps={{
                  "aria-label": capitalizeText(ft.type.replace(/_/g, " ")),
                }}
                sx={{
                  p: 0,
                  color: m.grey[50],
                  "&.Mui-checked": { color: m.ePurple[500] },
                }}
              />
            </Box>
          );
        })}
      </Box>

      {/* Update */}
      <Button
        fullWidth
        variant="contained"
        onClick={handleUpdate}
        sx={{
          mt: 1,
          bgcolor: m.ePurple[500],
          color: m.surface.white,
          textTransform: "none",
          fontSize: "0.875rem",
          fontWeight: 700,
          lineHeight: "20px",
          borderRadius: "8px",
          py: 2,
          boxShadow: "none",
          "&:hover": { bgcolor: m.ePurple[700], boxShadow: "none" },
        }}
      >
        Update
      </Button>
    </Drawer>
  );
};

export default MobileFieldTypeSheet;
